/**
 * Classify and prepare files for Printing Toolkit General print.
 * Images become single-page PDFs (QZ pdf path). Text/HTML become HTML jobs.
 */

import { buildFittedGeneralLetterHtml } from './generalPrintSizes';

export const GENERAL_PRINT_MAX_BYTES = 50 * 1024 * 1024;

export const GENERAL_PRINT_FILE_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.txt,.csv,.html,.htm,.docx,application/pdf,image/png,image/jpeg,image/webp,image/gif,image/bmp,text/plain,text/csv,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const MM_TO_PT = 72 / 25.4;

function extensionOf(fileName) {
  const name = String(fileName || '').toLowerCase();
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1) : '';
}

function mimeOf(file) {
  return String(file?.type || '')
    .toLowerCase()
    .trim();
}

/**
 * @param {File|Blob} file
 * @returns {'pdf'|'image'|'text'|'html'|'docx'|'unsupported'}
 */
export function classifyGeneralPrintFile(file) {
  if (!file) return 'unsupported';
  const ext = extensionOf(file.name);
  const mime = mimeOf(file);

  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (
    ext === 'docx' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'docx';
  }
  if (
    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'].includes(ext) ||
    mime.startsWith('image/')
  ) {
    return 'image';
  }
  if (['html', 'htm'].includes(ext) || mime === 'text/html') return 'html';
  if (['txt', 'csv', 'md', 'log'].includes(ext) || mime.startsWith('text/')) {
    return 'text';
  }
  return 'unsupported';
}

export function describeUnsupportedGeneralPrintFile(file) {
  const name = file?.name || 'That file';
  return `${name} is not a printable General print type. Use PDF, PNG, JPG, WEBP, GIF, BMP, TXT, CSV, HTML, or Word (.docx placeholder).`;
}

async function blobToPngBytes(blob) {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Unable to draw image for print.');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0);
      const pngBlob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) =>
            result
              ? resolve(result)
              : reject(new Error('Unable to encode image as PNG.')),
          'image/png',
        );
      });
      return new Uint8Array(await pngBlob.arrayBuffer());
    } finally {
      bitmap.close?.();
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Unable to decode image for print.'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to draw image for print.');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) =>
          result
            ? resolve(result)
            : reject(new Error('Unable to encode image as PNG.')),
        'image/png',
      );
    });
    return new Uint8Array(await pngBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Fit an image onto one page of the selected General print size as a PDF Blob.
 * @param {File|Blob} file
 * @param {{ widthMm?: number, heightMm?: number }} size
 * @returns {Promise<Blob>}
 */
export async function convertImageFileToPdfBlob(file, size) {
  if (!file?.size) throw new Error('The selected image is empty.');
  if (file.size > GENERAL_PRINT_MAX_BYTES) {
    throw new Error('Image exceeds the 50 MB safety limit.');
  }

  const { PDFDocument } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.create();
  const pageWidth = (Number(size?.widthMm) || 210) * MM_TO_PT;
  const pageHeight = (Number(size?.heightMm) || 297) * MM_TO_PT;
  const margin = 18;
  const maxW = Math.max(36, pageWidth - margin * 2);
  const maxH = Math.max(36, pageHeight - margin * 2);

  const ext = extensionOf(file.name);
  const mime = mimeOf(file);
  const bytes = new Uint8Array(await file.arrayBuffer());
  let embedded;
  try {
    if (ext === 'png' || mime === 'image/png') {
      embedded = await pdfDoc.embedPng(bytes);
    } else if (
      ['jpg', 'jpeg'].includes(ext) ||
      mime === 'image/jpeg' ||
      mime === 'image/jpg'
    ) {
      embedded = await pdfDoc.embedJpg(bytes);
    } else {
      embedded = await pdfDoc.embedPng(await blobToPngBytes(file));
    }
  } catch (error) {
    // Fall back through canvas for odd JPEG/PNG variants browsers can still decode.
    try {
      embedded = await pdfDoc.embedPng(await blobToPngBytes(file));
    } catch {
      throw new Error(
        error?.message || 'Unable to prepare that image for print.',
      );
    }
  }

  const scale = Math.min(maxW / embedded.width, maxH / embedded.height, 1);
  const drawW = embedded.width * scale;
  const drawH = embedded.height * scale;
  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  page.drawImage(embedded, {
    x: (pageWidth - drawW) / 2,
    y: (pageHeight - drawH) / 2,
    width: drawW,
    height: drawH,
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
}

/**
 * Build printable HTML from a text / CSV / HTML file.
 * @param {File|Blob} file
 * @param {{ widthMm?: number, heightMm?: number, label?: string }} size
 */
export async function buildGeneralPrintHtmlFromTextFile(file, size) {
  if (!file?.size) throw new Error('The selected file is empty.');
  if (file.size > GENERAL_PRINT_MAX_BYTES) {
    throw new Error('File exceeds the 50 MB safety limit.');
  }
  const kind = classifyGeneralPrintFile(file);
  const text = await file.text();
  if (kind === 'html') {
    const trimmed = String(text || '').trim();
    if (!trimmed) throw new Error('The HTML file is empty.');
    if (/<html[\s>]/i.test(trimmed)) return trimmed;
    const width = Number(size?.widthMm) || 210;
    const height = Number(size?.heightMm) || 297;
    return `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>@page{size:${width}mm ${height}mm;margin:12mm;}body{margin:0;font-family:Georgia,serif;}</style></head><body>${trimmed}</body></html>`;
  }
  return buildFittedGeneralLetterHtml({
    title: 'London Smoke & Cure',
    body: text,
    size,
  });
}

export async function inspectPdfBlob(blob) {
  if (!blob?.size) throw new Error('The selected file is empty.');
  if (blob.size > GENERAL_PRINT_MAX_BYTES) {
    throw new Error('PDF exceeds the 50 MB safety limit.');
  }
  const { PDFDocument } = await import('pdf-lib');
  const document = await PDFDocument.load(await blob.arrayBuffer(), {
    ignoreEncryption: false,
    updateMetadata: false,
  });
  return { pageCount: document.getPageCount() || 1 };
}
